import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {boolean} */
  #scrollUpdatePending = false;

  /** @type {HTMLElement | null} */
  #loaderNext = null;

  /** @type {HTMLElement | null} */
  #loaderPrevious = null;

  /** @type {string} */
  #restoreHash = '';

  // Clears the hash on bfcache restoration (back navigation). When the browser
  // restores a page from the Back-Forward Cache, scripts don't re-execute and
  // connectedCallback doesn't re-fire — only pageshow fires with event.persisted=true.
  #onPageShow = (event) => {
    if (event.persisted && window.location.hash) {
      const url = new URL(window.location.href);
      url.hash = '';
      history.replaceState(history.state, '', url.toString());
    }
  };

  connectedCallback() {
    super.connectedCallback();

    // The synchronous inline script in main-collection.liquid already cleared the
    // hash and stored it in sessionStorage before any module script loaded.
    // Read from there first; fall back to window.location.hash (e.g. pagination
    // mode where the inline script does not run).
    const _storedHash = sessionStorage.getItem('__restoreHash');
    if (_storedHash) {
      sessionStorage.removeItem('__restoreHash');
      this.#restoreHash = _storedHash;
    } else if (window.location.hash) {
      this.#restoreHash = window.location.hash;
      const cleanUrl = new URL(window.location.href);
      cleanUrl.hash = '';
      history.replaceState(history.state, '', cleanUrl.toString());
    }

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);

    this.#initLoaders();
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('pageshow', this.#onPageShow);

    if (this.#restoreHash && this.getAttribute('infinite-scroll') === 'true') {
      // On back-navigation the URL has a hash (#product-id) and scrollIntoView
      // runs on DOMContentLoaded to restore the scroll position.
      //
      // If we call #observeViewMore() immediately (scrollY=0, because
      // scrollRestoration:'manual' starts the page at the top), viewMorePrevious
      // is visible and the IntersectionObserver fires before scrollIntoView runs.
      // #renderPreviousPage then prepends at scrollY=0, scrollIntoView repositions
      // to the card's new absolute offset, and the RAF compensation over-corrects,
      // leaving the user at the wrong position.
      //
      // Fix: delay observer setup until DOMContentLoaded + one RAF so that
      // scrollIntoView has already set the correct scrollY before the observer
      // can fire and before any scroll compensation is calculated.
      const setupAfterRestore = () => {
        requestAnimationFrame(() => {
          this.#observeViewMore();
          // Also force-render the next page here: if the user is in the middle
          // of a page, viewMoreNext may be outside the 1000px rootMargin and
          // the observer would never fire without this nudge.
          this.#renderNextPage();
        });
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', setupAfterRestore, { once: true });
      } else {
        setupAfterRestore();
      }
    } else {
      this.#observeViewMore();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('pageshow', this.#onPageShow);
    this.#loaderNext?.remove();
    this.#loaderPrevious?.remove();
  }

  #onScroll = () => {
    if (this.#scrollUpdatePending) return;
    this.#scrollUpdatePending = true;
    requestAnimationFrame(() => {
      this.#scrollUpdatePending = false;
      const midX = window.innerWidth / 2;
      const midY = window.innerHeight / 2;
      const el = document.elementFromPoint(midX, midY);
      const card = el?.closest('[ref="cards[]"]');
      if (!card) return;
      const page = card.dataset.page;
      if (!page) return;
      const url = new URL(window.location.href);
      if (url.searchParams.get('page') === page) return;
      url.searchParams.set('page', page);
      url.hash = '';
      history.replaceState('', '', url.toString());
    });
  };

  #initLoaders() {
    const createLoader = () => {
      const el = document.createElement('div');
      el.className = 'paginated-list-loader';
      el.setAttribute('aria-hidden', 'true');
      el.innerHTML = `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-dasharray="42 15"/>
      </svg>`;
      return el;
    };

    const { viewMorePrevious, viewMoreNext } = this.refs;

    if (viewMorePrevious) {
      this.#loaderPrevious = createLoader();
      this.#loaderPrevious.classList.add('paginated-list-loader--previous');
      viewMorePrevious.insertAdjacentElement('afterend', this.#loaderPrevious);
    }

    if (viewMoreNext) {
      this.#loaderNext = createLoader();
      this.#loaderNext.classList.add('paginated-list-loader--next');
      viewMoreNext.insertAdjacentElement('beforebegin', this.#loaderNext);
    }
  }

  #showLoader(position) {
    const loader = position === 'next' ? this.#loaderNext : this.#loaderPrevious;
    loader?.classList.add('paginated-list-loader--visible');
  }

  #hideLoader(position) {
    const loader = position === 'next' ? this.#loaderNext : this.#loaderPrevious;
    loader?.classList.remove('paginated-list-loader--visible');
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '1000px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      this.#showLoader('next');
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      this.#hideLoader('next');
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    // Consume the stored hash (already removed from URL in connectedCallback).
    const restoreHash = this.#restoreHash;
    this.#restoreHash = '';

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      this.#showLoader('previous');
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      this.#hideLoader('previous');
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Capture the absolute document position of the first visible element
    // before prepending (scroll-independent: .top + scrollY = absolute y).
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    requestAnimationFrame(() => {
      if (!firstElement) return;

      // When returning from a product page the hash identifies the exact card
      // the user was viewing. Re-applying scrollIntoView after the prepend is
      // simpler and more reliable than computing a height diff: it positions
      // the card precisely regardless of lazy-image sizes or layout shifts.
      if (restoreHash) {
        const targetCard = document.getElementById(restoreHash.slice(1));
        if (targetCard) {
          targetCard.scrollIntoView({ behavior: 'instant' });
          return;
        }
      }

      // Normal case (no hash / card not found): compensate mathematically so
      // the viewport stays fixed while the prepended content appears above.
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: window.scrollY + heightDiff,
        behavior: 'instant',
      });
    });

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > .product-grid__item');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
